import React, { useEffect, useRef, useState } from 'react';

import { Add, ArrowRight, CTA, ThemeProvider } from '@vw-marketing/us-components';

import { GarageVehicleCardView } from '../../../../shared/components/vehicle-card/GarageVehicleCard/GarageVehicleCardView';
import {
  StyledFixedFooter,
  StyledGarageCardsContainer,
  StyledGarageLayerWrapper,
} from './styles';
import {
  onVWBasicVehicleSelectClickEvent,
  onVWBasicStepNavigationClickEvent,
} from '../../../../shared/tagging/tagging-helpers';
import { Vehicles } from '../../../../shared/typings/main';

export interface GarageLayerContentViewProps {
  vehicles: Vehicles[];
  preferredVehicle: any | null;
  translationHandler: (key: string) => string;
  trackingManager: any;
  onVehicleAction: (vehicleId: string, isSelectVehicle: boolean) => Promise<void>;
  onAddVehicle: () => void;
  onNavigateToVehicleManagement: () => void;
}

export const GarageLayerContentView = ({
  vehicles,
  preferredVehicle,
  translationHandler,
  trackingManager,
  onVehicleAction,
  onAddVehicle,
  onNavigateToVehicleManagement,
}: GarageLayerContentViewProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);

  const selectedCardRef = useRef<HTMLDivElement>(null);
  const cardsContainerRef = useRef<HTMLDivElement>(null);

  const getInitialSelection = () => {
    if (preferredVehicle?.vehicleId) return preferredVehicle.vehicleId;
    if (vehicles?.length === 1) return vehicles[0].vehicleId;
    return '';
  };

  const [displayVehicles, setDisplayVehicles] = useState<Vehicles[]>(vehicles);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>(
    getInitialSelection(),
  );

  useEffect(() => {
    if (!loading && vehicles.length > 0) {
      setDisplayVehicles(vehicles);
    } else if (!loading && vehicles.length === 0 && preferredVehicle) {
      // @ts-ignore
      setDisplayVehicles((prev) => {
        const exists = prev.some(
          (v) => v.vehicleId === preferredVehicle.vehicleId,
        );
        if (exists) return prev;
        return [...prev, preferredVehicle];
      });
    } else {
      setDisplayVehicles([]);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (cardsContainerRef.current) {
        setHasScrolled(cardsContainerRef.current.scrollTop > 0);
      }
    };
    const container = cardsContainerRef.current;
    if (container) container.addEventListener('scroll', handleScroll);
    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedCardRef.current && cardsContainerRef.current) {
        const container = cardsContainerRef.current;
        const card = selectedCardRef.current;
        const containerRect = container.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        const scrollTop = container.scrollTop;
        const offset = cardRect.top - containerRect.top + scrollTop - 20;
        container.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleVehicleSelect = (vehicleId: string) => {
    setSelectedVehicleId(vehicleId);
  };

  const handleVehicleAction = async (isSelectVehicle: boolean) => {
    if (loading) return;

    if (displayVehicles.length === 1) {
      onAddVehicle();
      return;
    }

    if (!selectedVehicleId) return;

    if (isSelectVehicle) {
      onVWBasicVehicleSelectClickEvent(trackingManager, {
        Name: 'Selected Vehicle',
        StepName: 'Garage',
        StepNumber: '2',
      });
    }

    setLoading(true);
    try {
      await onVehicleAction(selectedVehicleId, isSelectVehicle);
    } catch (_error) {
      // onVehicleAction handles error toast internally
    } finally {
      setLoading(false);
    }
  };

  const hasScroll = displayVehicles.length >= 3;

  return (
    <ThemeProvider theme="main">
      <StyledGarageLayerWrapper>
        <StyledGarageCardsContainer
          ref={cardsContainerRef}
          scroll={hasScroll}
          hasScrolled={hasScrolled && hasScroll}
        >
          {displayVehicles.map((vehicle: Vehicles) => (
            <GarageVehicleCardView
              key={vehicle.vehicleId}
              vehicle={vehicle}
              isSelected={selectedVehicleId === vehicle.vehicleId}
              onSelect={handleVehicleSelect}
              translationHandler={translationHandler}
              ref={
                selectedVehicleId === vehicle.vehicleId ? selectedCardRef : null
              }
            />
          ))}
        </StyledGarageCardsContainer>
        <StyledFixedFooter scroll={hasScroll}>
          <CTA
            disabled={loading}
            emphasis="primary"
            icon={
              displayVehicles.length === 1 ? (
                <Add variant="small" />
              ) : undefined
            }
            tag="button"
            onClick={async () => {
              await handleVehicleAction(displayVehicles.length !== 1);
            }}
          >
            {translationHandler(
              displayVehicles.length === 1
                ? 'vehicleManagement.addVehicleButton'
                : 'garage.selectVehicle.cta.label',
            )}
          </CTA>
          <CTA
            disabled={loading}
            emphasis="secondary"
            icon={<ArrowRight variant="small" />}
            iconPosition="end"
            tag="button"
            onClick={() => {
              onVWBasicStepNavigationClickEvent(trackingManager, {
                Name: 'Selected Vehicle',
                StepName: 'Garage',
                StepNumber: '2',
              });
              onNavigateToVehicleManagement();
            }}
          >
            {translationHandler('garage.goTo.vehicleManagement.cta.label')}
          </CTA>
        </StyledFixedFooter>
      </StyledGarageLayerWrapper>
    </ThemeProvider>
  );
};
